import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { CartContext } from '../../contexts/CartContext';
import ProductCard from '../../components/ProductCard';
import LoadingSpinner from '../../components/LoadingSpinner';
import './User.css';

// ✅ Public axios — token olmadan, guest ke liye bhi kaam karta hai
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 15000,
});

export default function Home() {
  const { api, user } = useContext(AuthContext);
  const { addToCart }  = useContext(CartContext);

  const [products, setProducts]                 = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState('');
  const [searchQuery, setSearchQuery]           = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [trendingLoading, setTrendingLoading]   = useState(false);
  const [toasts, setToasts]                     = useState([]);

  useEffect(() => { fetchProducts(); }, [selectedCategory]);

  useEffect(() => {
    if (selectedCategory === 'all' && searchQuery === '') fetchTrending();
  }, [products, selectedCategory, searchQuery]);

  useEffect(() => { filterProducts(); }, [products, searchQuery]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};

      // ✅ publicApi use karo — guest bhi dekh sakta hai
      const response = await publicApi.get('/products', { params });
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.products || []);
      setProducts(data);
      setFilteredProducts(data);
    } catch (err) {
      console.error('Fetch products error:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrending = async () => {
    try {
      setTrendingLoading(true);
      try {
        // ✅ publicApi use karo
        const response = await publicApi.get('/products/trending');
        const data = Array.isArray(response.data)
          ? response.data
          : (response.data?.products || response.data?.trending || []);
        if (data.length > 0) {
          setTrendingProducts(data.slice(0, 6));
          setTrendingLoading(false);
          return;
        }
      } catch (e) {}
      setTrendingProducts(products.slice(0, 6));
    } catch (err) {
      setTrendingProducts([]);
    } finally {
      setTrendingLoading(false);
    }
  };

  const filterProducts = () => {
    if (!searchQuery.trim()) { setFilteredProducts(products); return; }
    const query    = searchQuery.toLowerCase().trim();
    const filtered = products.filter(p =>
      p.name?.toLowerCase().includes(query) ||
      p.description?.toLowerCase().includes(query) ||
      p.category?.toLowerCase().includes(query)
    );
    setFilteredProducts(filtered);
  };

  const getEffectivePrice = (product) => {
    const price = parseFloat(product.price);
    const disc  = parseFloat(product.discount_percent);
    if (!disc || isNaN(disc) || disc <= 0) return price;
    return parseFloat((price - (price * disc) / 100).toFixed(2));
  };

  const showToast = (product, qty, totalAmount) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, product, qty, totalAmount, visible: false }]);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setToasts(prev => prev.map(t => t.id === id ? { ...t, visible: true } : t));
      });
    });

    setTimeout(() => {
      setToasts(prev => prev.map(t => t.id === id ? { ...t, visible: false } : t));
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, 450);
    }, 3000);
  };

  // ✅ Guest cart mein add kar sakta hai, checkout pe login required hoga
  const handleAddToCart = (product, qty = 1) => {
    if (product.stock <= 0) { alert('This product is out of stock'); return; }
    addToCart(product, qty);
    const unitPrice   = getEffectivePrice(product);
    const totalAmount = (unitPrice * qty).toFixed(2);
    showToast(product, qty, totalAmount);
  };

  const showTrending = selectedCategory === 'all' && searchQuery === '';

  if (loading) return <LoadingSpinner message="Loading products..." />;

  return (
    <div className="user-home">
      <div className="container">

        {/* Header */}
        <div className="home-header">
          <div className="header-content">
            <h1 className="page-title">
              <span className="title-icon">🛒</span>
              Fresh Delivery of Products
            </h1>
            <p className="page-subtitle">Farm fresh delivered to your doorstep</p>
          </div>

          {/* ✅ Guest ko login/register buttons dikhao */}
          {!user && (
            <div className="guest-actions">
              <a href="/login"    className="btn btn-outline btn-sm">🔑 Login</a>
              <a href="/register" className="btn btn-primary btn-sm">📝 Register</a>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="search-section">
          <div className="search-container">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search for products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>
        </div>

        {/* Categories */}
        <div className="category-section">
          <div className="category-buttons">
            {[
              { key: 'all',        label: '🛒 All Products' },
              { key: 'fruits',     label: '🍎 Fruits'        },
              { key: 'vegetables', label: '🥬 Vegetables'    },
              { key: 'meat',       label: '🥩 Meat'          },
            ].map(c => (
              <button
                key={c.key}
                className={`category-btn ${selectedCategory === c.key ? 'active' : ''}`}
                onClick={() => setSelectedCategory(c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Trending */}
        {showTrending && (
          <div className="trending-section">
            <div className="section-header">
              <h2 className="section-title">
                <span className="section-icon">🔥</span>
                Trending Products
              </h2>
              {trendingLoading && <span className="loading-badge">Loading...</span>}
            </div>
            {trendingProducts.length > 0 ? (
              <div className="trending-grid">
                {trendingProducts.map(product => (
                  <div key={product.id} className="trending-item">
                    <ProductCard product={product} onAddToCart={handleAddToCart} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-trending"><p>No trending products yet</p></div>
            )}
          </div>
        )}

        {/* All Products */}
        <div className="products-section">
          <div className="section-header">
            <h2 className="section-title">
              <span className="section-icon">🛍️</span>
              {selectedCategory === 'all'        ? 'All Products'   :
               selectedCategory === 'fruits'     ? 'Fresh Fruits'   :
               selectedCategory === 'meat'       ? 'Fresh Meat'     : 'Fresh Vegetables'}
            </h2>
            <span className="product-count">{filteredProducts.length} items</span>
          </div>

          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span className="alert-text">{error}</span>
            </div>
          )}

          {filteredProducts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🔎</div>
              <h3 className="empty-state-title">No products found</h3>
              <p className="empty-state-text">
                {searchQuery
                  ? `No products match "${searchQuery}"`
                  : 'No products available in this category'}
              </p>
              {searchQuery && (
                <button className="btn btn-primary" onClick={() => setSearchQuery('')}>
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div className="products-grid">
              {filteredProducts.map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          )}
        </div>

      </div>

      {/* Toast Stack */}
      <div className="toast-stack">
        {toasts.map(t => (
          <div
            key={t.id}
            className={`toast-slide ${t.visible ? 'toast-in' : 'toast-out'}`}
          >
            <span className="toast-check">✅</span>
            <div className="toast-body">
              <span className="toast-name">{t.product.name}</span>
              <span className="toast-sub">
                {t.qty} item{t.qty > 1 ? 's' : ''} added to cart
              </span>
            </div>
            <div className="toast-amount">
              <span className="toast-amount-label">Total</span>
              <span className="toast-amount-value">Rs. {t.totalAmount}</span>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}